// NewsPage.jsx - Version complète avec affichage synthèse corrigé
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TrendingUp, Settings, Bell, X, Search, CheckCircle, RefreshCw, Clock } from 'lucide-react';

// Import des modules
import NewsHeader from './NewsHeader';
import NewsThemes from './NewsThemes';
import NewsContent from './NewsContent';
import NewsWatchlistManager from './NewsWatchListManager';
import newsFirebaseService from './NewsFirebaseService';

// Hooks personnalisés
import { useNewsData } from './useNewsData';
import { useObjectives } from './useObjectives';

// Services IA pour la synthèse
// import perplexityNewsService from './PerplexityNewsService';
// import newsRetentionService from './NewsRetentionService';
import { askGPT } from '../components/OpenAIService';

const NewsPage = ({ 
  userId, 
  userRole = 'user', 
  userEmail,
  objectiveId = null,
  objectiveTitle = null,
  objectiveTheme = null 
}) => {
  // ================================
  // CONSTANTES ET CONFIGURATION
  // ================================

  const ALADINN_THEME_MAPPING = {
    'Activités sportives': 'sports',
    'Activité ludique et créative': 'creativity', 
    'Voyages': 'travel',
    'Finance et Banque': 'finance',
    'Développement entrepreneurial': 'entrepreneurship',
    'Evolution de carrière': 'career',
    'Santé et bien-être': 'health',
    'Formation et apprentissage': 'education',
    'Gestion immobilière': 'realestate',
    'Développement personnel': 'personal'
  };

  const NEWS_THEMES = [
    { id: 'sports', label: 'Sport', icon: '⚽', color: 'bg-orange-500', textColor: 'text-orange-800', bgLight: 'bg-orange-50', borderColor: 'border-orange-200', description: 'Actualités sportives et fitness' },
    { id: 'creativity', label: 'Créativité & Loisirs', icon: '🎨', color: 'bg-pink-500', textColor: 'text-pink-800', bgLight: 'bg-pink-50', borderColor: 'border-pink-200', description: 'Arts, culture et activités créatives' },
    { id: 'travel', label: 'Voyages', icon: '✈️', color: 'bg-cyan-500', textColor: 'text-cyan-800', bgLight: 'bg-cyan-50', borderColor: 'border-cyan-200', description: 'Destinations et conseils voyage' },
    { id: 'finance', label: 'Finance', icon: '💰', color: 'bg-amber-500', textColor: 'text-amber-800', bgLight: 'bg-amber-50', borderColor: 'border-amber-200', description: 'Marchés financiers et investissement' },
    { id: 'entrepreneurship', label: 'Entrepreneuriat', icon: '🚀', color: 'bg-blue-500', textColor: 'text-blue-800', bgLight: 'bg-blue-50', borderColor: 'border-blue-200', description: 'Business et innovation' },
    { id: 'career', label: 'Carrière', icon: '📈', color: 'bg-purple-500', textColor: 'text-purple-800', bgLight: 'bg-purple-50', borderColor: 'border-purple-200', description: 'Evolution professionnelle' },
    { id: 'health', label: 'Santé & Bien-être', icon: '🌱', color: 'bg-emerald-500', textColor: 'text-emerald-800', bgLight: 'bg-emerald-50', borderColor: 'border-emerald-200', description: 'Santé et mode de vie' },
    { id: 'education', label: 'Formation', icon: '📚', color: 'bg-green-500', textColor: 'text-green-800', bgLight: 'bg-green-50', borderColor: 'border-green-200', description: 'Apprentissage et développement' },
    { id: 'realestate', label: 'Immobilier', icon: '🏠', color: 'bg-indigo-500', textColor: 'text-indigo-800', bgLight: 'bg-indigo-50', borderColor: 'border-indigo-200', description: 'Marché immobilier' },
    { id: 'personal', label: 'Développement Personnel', icon: '🧠', color: 'bg-teal-500', textColor: 'text-teal-800', bgLight: 'bg-teal-50', borderColor: 'border-teal-200', description: 'Croissance personnelle' }
  ];

  const FONT_STYLES = `
    .page-title { font-size: 1.75rem; line-height: 2.25rem; font-weight: 700; }
    .section-title { font-size: 1.5rem; line-height: 2rem; font-weight: 600; }
    .main-text { font-size: 1.125rem; line-height: 1.75rem; }
    .button-text { font-size: 1rem; }
    .input-text { font-size: 1.125rem !important; }
  `;

  // ================================
  // ÉTATS PRINCIPAUX
  // ================================
  
  const [selectedTheme, setSelectedTheme] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showWatchlist, setShowWatchlist] = useState(false);
  const [selectedObjectiveForNews, setSelectedObjectiveForNews] = useState(null);
  const [showObjectiveDropdown, setShowObjectiveDropdown] = useState(false);
  
  // États pour la synthèse GPT
  const [newsSynthesis, setNewsSynthesis] = useState('');
  const [loadingSynthesis, setLoadingSynthesis] = useState(false);
  const [synthesisArticles, setSynthesisArticles] = useState([]);
  const [enrichmentInProgress, setEnrichmentInProgress] = useState(false);

  // États pour la découverte automatique
  const [autoDiscoveryTriggered, setAutoDiscoveryTriggered] = useState(false);
  const [showIndicatorsForObjective, setShowIndicatorsForObjective] = useState(true);

  // Référence pour tracker les synthèses générées et le dernier objectif
  const synthesisTracker = useRef(new Set());
  const lastObjectiveId = useRef(null);

  // ================================
  // HOOKS PERSONNALISÉS
  // ================================

  const {
    newsByTheme, filteredNews, loading: newsLoading, error: newsError, isDiscovering, discoveryMessage,
    semanticAnalysisInfo, currentSearchMode, loadNews, handleDiscoverTheme, handleDiscoverForObjective,
    handleCustomDiscovery, handleReadNews,
    // Données de persistance
    storedObjectiveResults, hasStoredResults, loadingStoredResults,
    handleRefreshObjectiveResults, getStoredResultsInfo,
    // Notifications de mise à jour
    dataUpdated, lastUpdateType
  } = useNewsData(userId, selectedTheme, searchQuery, selectedObjectiveForNews, NEWS_THEMES);

  const { availableObjectives, loadingObjectives, objectivesError, loadUserObjectives } = useObjectives(userId);

  // ================================
  // CONDITIONS D'AFFICHAGE
  // ================================

  const shouldShowEmptyState = useMemo(() => {
    return !selectedObjectiveForNews && 
           !isDiscovering && 
           !enrichmentInProgress && 
           filteredNews.length === 0 && 
           !newsLoading &&
           selectedTheme === 'all';
  }, [selectedObjectiveForNews, isDiscovering, enrichmentInProgress, filteredNews.length, newsLoading, selectedTheme]);

  // ================================
  // FONCTIONS UTILITAIRES
  // ================================

  const getNewsThemeFromObjective = (objectiveTheme) => {
    return ALADINN_THEME_MAPPING[objectiveTheme] || 'entrepreneurship';
  };

  const getObjectiveDisplayName = (objective) => {
    if (!objective) return 'Objectif sans nom';
    return objective.name || objective.shortTitle || objective.title || 'Objectif sans nom';
  };

  const handleResetSynthesis = useCallback(() => {
    console.log('🔄 Reset synthèse');
    setNewsSynthesis('');
    setSynthesisArticles([]);
    synthesisTracker.current.clear();
  }, []);

  const handleThemeSelect = async (themeId) => {
    console.log('🎯 Changement thème:', themeId);
    setSelectedTheme(themeId);
    setSearchQuery('');
    handleResetSynthesis();
  };

  const handleSearchQueryChange = (query) => {
    setSearchQuery(query);
    handleResetSynthesis();
  };

  // ================================
  // GÉNÉRATION SYNTHÈSE AVEC AFFICHAGE CORRIGÉ
  // ================================

  const generateNewsSynthesis = useCallback(async (forceGeneration = false) => {
    // Vérifier articles disponibles
    if (filteredNews.length === 0) {
      console.log('ℹ️ Aucun article, affichage message approprié');
      
      if (selectedObjectiveForNews) {
        setNewsSynthesis(`**Aucune source pertinente trouvée**

Notre analyse n'a identifié aucune actualité pertinente pour l'objectif "${getObjectiveDisplayName(selectedObjectiveForNews)}".

**Actions suggérées :**
- Cliquez sur "Enrichir" pour découvrir de nouvelles actualités
- Modifiez votre objectif avec des termes plus génériques
- Consultez les actualités par thème ci-dessous`);
      } else if (currentSearchMode === 'custom' && searchQuery) {
        setNewsSynthesis(`**Aucune source trouvée pour "${searchQuery}"**

Votre recherche personnalisée n'a donné aucun résultat.

**Suggestions :**
- Essayez avec des termes différents
- Élargissez votre recherche
- Consultez les actualités par thème`);
      }
      
      setSynthesisArticles([]);
      return;
    }

    if (loadingSynthesis) {
      console.log('ℹ️ Synthèse en cours, annulation');
      return;
    }

    // CORRECTION: Si synthèse stockée disponible et pas de force, l'utiliser
    if (!forceGeneration && selectedObjectiveForNews && storedObjectiveResults?.synthesis) {
      console.log('📂 Utilisation synthèse stockée existante');
      setNewsSynthesis(storedObjectiveResults.synthesis);
      
      // Synchroniser les sources
      const articlesForSynthesis = filteredNews.slice(0, 20);
      setSynthesisArticles(articlesForSynthesis);
      return;
    }

    // Signature pour éviter régénération inutile
    const synthesisSignature = JSON.stringify({
      objectiveId: selectedObjectiveForNews?.id,
      articleIds: filteredNews.slice(0, 20).map(a => a.id).sort(),
      searchQuery: searchQuery,
      theme: selectedTheme
    });

    // Reset pour nouvel objectif
    if (selectedObjectiveForNews?.id !== lastObjectiveId.current) {
      console.log('🔄 Nouvel objectif, reset tracker');
      synthesisTracker.current.clear();
      lastObjectiveId.current = selectedObjectiveForNews?.id;
    }

    // Éviter régénération si pas forcée
    if (!forceGeneration && synthesisTracker.current.has(synthesisSignature)) {
      console.log('🛡 Synthèse déjà générée, synchronisation sources');
      // Synchroniser les sources même si synthèse existe
      const articlesForSynthesis = selectedObjectiveForNews 
        ? filteredNews.slice(0, 20)
        : filteredNews.slice(0, 15);
      setSynthesisArticles(articlesForSynthesis);
      return;
    }

    // Marquer comme en cours
    synthesisTracker.current.add(synthesisSignature);

    try {
      console.log('🤖 Génération synthèse avec données actuelles');
      setLoadingSynthesis(true);
      
      let articlesForSynthesis = [];
      let synthesisContext = '';
      let qualityInfo = null;
      
      if (selectedObjectiveForNews) {
        articlesForSynthesis = filteredNews.slice(0, 20);
        qualityInfo = {
          totalAnalyzed: semanticAnalysisInfo?.totalAnalyzed || 0,
          relevantFound: filteredNews.length,
          objective: selectedObjectiveForNews.title,
          avgScore: semanticAnalysisInfo?.avgScore || 0
        };
        synthesisContext = `personnalisées pour l'objectif "${getObjectiveDisplayName(selectedObjectiveForNews)}" (analyse sémantique)`;
      } else {
        articlesForSynthesis = filteredNews.slice(0, 15);
        if (searchQuery && searchQuery.trim()) {
          synthesisContext = `sur la recherche personnalisée "${searchQuery}"`;
        } else {
          synthesisContext = selectedTheme === 'all' ? 'générales' : `sur le thème "${NEWS_THEMES.find(t => t.id === selectedTheme)?.label}"`;
        }
      }
      
      // Synchroniser sources IMMÉDIATEMENT
      setSynthesisArticles(articlesForSynthesis);
      console.log(`🔗 Sources synchronisées: ${articlesForSynthesis.length} articles`);
      
      const newsContext = articlesForSynthesis.map((article, index) => {
        const scoreInfo = article.semanticScore ? ` [Score: ${article.semanticScore}/10]` : '';
        return `[${index + 1}] "${article.title}" - ${article.content || 'Pas de contenu'} (Source: ${article.source}) - URL: ${article.url}${scoreInfo}`;
      }).join('\n');

      const objectiveContext = selectedObjectiveForNews 
        ? `\n\nCONTEXTE IMPORTANT : L'utilisateur travaille sur l'objectif suivant : "${getObjectiveDisplayName(selectedObjectiveForNews)}".
           
           **ANALYSE SÉMANTIQUE** : ${qualityInfo.totalAnalyzed} articles analysés par IA, ${qualityInfo.relevantFound} jugés pertinents.
           Score sémantique moyen : ${qualityInfo.avgScore}/10.
           Ces articles ont été sélectionnés par analyse sémantique avancée pour leur pertinence spécifique à cet objectif.
           
           Ta synthèse doit être SPÉCIFIQUEMENT orientée vers cet objectif et montrer comment les actualités peuvent l'aider à progresser.`
        : searchQuery && searchQuery.trim() 
          ? `\n\nCONTEXTE RECHERCHE : L'utilisateur a recherché spécifiquement "${searchQuery}".
             Ta synthèse doit être orientée vers cette requête et analyser comment les actualités trouvées répondent à cette recherche.`
          : '';

      const prompt = `Voici les dernières actualités ${synthesisContext} ANALYSÉES SÉMANTIQUEMENT :

${newsContext}${objectiveContext}

${selectedObjectiveForNews ? 
`FOCUS SPÉCIAL : Cette synthèse doit être entièrement orientée vers l'objectif "${getObjectiveDisplayName(selectedObjectiveForNews)}". 
Analyse comment chaque tendance ou information peut aider l'utilisateur à progresser vers cet objectif.
Donne des conseils pratiques et des insights directement applicables à cet objectif.

**QUALITÉ DES SOURCES** : Les ${articlesForSynthesis.length} articles utilisés ont été sélectionnés par analyse sémantique IA pour leur pertinence maximale à cet objectif (score moyen: ${qualityInfo.avgScore}/10).` : 
searchQuery && searchQuery.trim() ? 
`FOCUS RECHERCHE : Cette synthèse doit répondre à la requête "${searchQuery}".
Analyse les tendances et informations trouvées en relation avec cette recherche spécifique.
Donne des insights pertinents et des conclusions pratiques sur le sujet recherché.` :
'Analyse les tendances générales et donne des insights pertinents.'}

IMPORTANT POUR LES LIENS : Dans ta synthèse, quand tu mentionnes des sujets spécifiques ou des tendances, utilise le format markdown suivant pour créer des liens vers les articles pertinents :
- Format requis : [texte descriptif précis et spécifique](URL_COMPLETE_DE_L_ARTICLE)
- OBLIGATOIRE : Utilise toujours un texte descriptif spécifique pour le lien, JAMAIS "source", "cet article" ou "cette étude"
- EXEMPLES CORRECTS : [nouvelles technologies en IA](url), [stratégies de financement innovantes](url), [tendances du marché immobilier 2024](url)
- EXEMPLES INCORRECTS : [cette source](url), [cet article](url), [voir ici](url)

La synthèse doit :
1. **${selectedObjectiveForNews ? `Être entièrement centrée sur l'objectif "${getObjectiveDisplayName(selectedObjectiveForNews)}"` : searchQuery ? `Répondre à la recherche "${searchQuery}"` : 'Identifier les tendances principales'}** avec des liens vers les articles sources
2. **${selectedObjectiveForNews ? 'Donner des conseils pratiques et actionables' : 'Résumer les points clés'}** en citant les sources pertinentes avec leurs URLs
3. **Être concise** (maximum 300 mots)
4. **Être rédigée de manière engageante** avec des insights pertinents
5. **Intégrer naturellement 4-6 liens markdown** vers les articles les plus pertinents avec des textes descriptifs spécifiques

Format souhaité : paragraphes courts avec des insights pertinents et des liens markdown intégrés naturellement avec des textes descriptifs précis.`;

      // Utiliser OpenAI pour la synthèse (Perplexity supprimé)
      const synthesis = await askGPT(prompt);
      
      setNewsSynthesis(synthesis);
      console.log('✅ Synthèse générée et affichée');

      // Sauvegarder si objectif
      if (selectedObjectiveForNews && synthesis && userId) {
        try {
          const selectedNewsIds = articlesForSynthesis.map(news => news.id);
          
          await newsFirebaseService.saveObjectiveNewsResults(
            selectedObjectiveForNews,
            selectedNewsIds,
            synthesis,
            semanticAnalysisInfo,
            userId
          );
          
          console.log('💾 Synthèse sauvegardée');
        } catch (saveError) {
          console.warn('⚠️ Erreur sauvegarde:', saveError);
        }
      }
      
    } catch (error) {
      console.error('❌ Erreur génération synthèse:', error);
      setNewsSynthesis('Erreur lors de la génération de la synthèse. Veuillez réessayer.');
      synthesisTracker.current.delete(synthesisSignature);
    } finally {
      setLoadingSynthesis(false);
    }
  }, [
    filteredNews, 
    selectedObjectiveForNews, 
    currentSearchMode, 
    searchQuery, 
    selectedTheme, 
    semanticAnalysisInfo, 
    loadingSynthesis,
    userId,
    storedObjectiveResults
  ]);

  // ================================
  // DÉCOUVERTE PERSONNALISÉE SANS GÉNÉRATION AUTO
  // ================================

  const handleCustomDiscoveryInternal = useCallback(async (searchQuery, theme = null) => {
    try {
      console.log('🔍 Découverte personnalisée:', { searchQuery, theme });
      
      if (!userId || !searchQuery?.trim()) {
        throw new Error('Paramètres invalides pour la découverte');
      }

      handleResetSynthesis();
      
      const result = await handleCustomDiscovery(searchQuery.trim(), theme);
      console.log('📊 Résultat découverte personnalisée:', result);

      if (result?.success && result?.dataReady) {
        console.log('✅ Découverte personnalisée réussie - AUCUNE synthèse automatique');
        return result;
      } else {
        setNewsSynthesis(`**Aucun résultat trouvé pour "${searchQuery}"**

Notre système n'a trouvé aucune actualité pertinente pour cette requête.

**Suggestions :**
- Essayez avec des termes plus génériques
- Variez les mots-clés utilisés
- Consultez les actualités générales par thème`);
        setSynthesisArticles([]);
        return result;
      }

    } catch (error) {
      console.error('❌ Erreur découverte personnalisée:', error);
      setNewsSynthesis(`**Erreur lors de la découverte**

${error.message}

Veuillez réessayer avec une autre requête.`);
      setSynthesisArticles([]);
      return { success: false, error: error.message };
    }
  }, [userId, handleCustomDiscovery, handleResetSynthesis]);

  // ================================
  // DÉCOUVERTE OBJECTIF SANS GÉNÉRATION AUTO
  // ================================

  const handleDiscoverForObjectiveWithCheck = useCallback(async (objective) => {
    console.log('🔄 Enrichissement objectif:', getObjectiveDisplayName(objective));
    
    handleResetSynthesis();
    setEnrichmentInProgress(true);
    
    try {
      const result = await handleDiscoverForObjective(objective, false);
      console.log('📊 Résultat découverte objectif:', result);
      
      if (result?.success && result?.dataReady) {
        console.log('✅ Enrichissement objectif réussi - AUCUNE synthèse automatique');
        return result;
      } else {
        console.log('⚠️ Enrichissement sans résultat');
        setNewsSynthesis(`**Aucune actualité trouvée pour l'objectif "${getObjectiveDisplayName(objective)}"**

Notre système d'enrichissement IA n'a trouvé aucun article pertinent pour cet objectif.

**Suggestions :**
- Reformulez votre objectif avec des termes plus génériques
- Consultez les actualités générales par thème
- Relancez une découverte plus tard`);
        setSynthesisArticles([]);
        return result;
      }
    } catch (error) {
      console.error('❌ Erreur enrichissement:', error);
      setNewsSynthesis('**Erreur lors de la découverte**\n\nUne erreur est survenue. Veuillez réessayer.');
      setSynthesisArticles([]);
      throw error;
    } finally {
      setEnrichmentInProgress(false);
    }
  }, [handleDiscoverForObjective, handleResetSynthesis]);

  // ================================
  // DÉCOUVERTE AUTOMATIQUE
  // ================================

  const triggerAutoDiscovery = useCallback(async (objective) => {
    if (!objective || autoDiscoveryTriggered) return;

    console.log('🚀 Découverte automatique:', getObjectiveDisplayName(objective));
    
    setAutoDiscoveryTriggered(true);
    setShowIndicatorsForObjective(false);
    
    try {
      const result = await handleDiscoverForObjectiveWithCheck(objective);
      
      if (result?.success) {
        console.log('✅ Découverte automatique réussie');
        setShowIndicatorsForObjective(true);
      } else {
        console.log('⚠️ Découverte automatique sans résultat');
        setShowIndicatorsForObjective(true);
      }
    } catch (error) {
      console.error('❌ Erreur découverte automatique:', error);
      setShowIndicatorsForObjective(true);
    }
  }, [autoDiscoveryTriggered, handleDiscoverForObjectiveWithCheck]);

  const shouldTriggerAutoDiscovery = useMemo(() => {
    return selectedObjectiveForNews && 
           !autoDiscoveryTriggered && 
           !hasStoredResults && 
           filteredNews.length === 0 && 
           !isDiscovering && 
           !newsLoading &&
           Object.keys(newsByTheme).length > 0;
  }, [
    selectedObjectiveForNews, 
    autoDiscoveryTriggered, 
    hasStoredResults, 
    filteredNews.length, 
    isDiscovering, 
    newsLoading,
    newsByTheme
  ]);

  // ================================
  // ACTUALISATION FORCÉE
  // ================================

  const handleRefreshResults = useCallback(async (objective) => {
    console.log('🔄 Actualisation forcée:', getObjectiveDisplayName(objective));
    
    handleResetSynthesis();
    setEnrichmentInProgress(true);
    
    try {
      const result = await handleRefreshObjectiveResults(objective);
      console.log('📊 Résultat actualisation:', result);
      
      if (result?.success && result?.dataReady) {
        console.log('✅ Actualisation réussie');
        // Générer synthèse après actualisation
        setTimeout(() => {
          generateNewsSynthesis(true);
        }, 1500);
        
        return result;
      } else {
        console.log('⚠️ Actualisation sans résultat');
        setNewsSynthesis(`**Aucune nouvelle actualité trouvée**

L'actualisation n'a pas permis de trouver de nouvelles actualités pertinentes pour l'objectif "${getObjectiveDisplayName(objective)}".

Les données précédentes restent disponibles.`);
        return result;
      }
    } catch (error) {
      console.error('❌ Erreur actualisation:', error);
      setNewsSynthesis('**Erreur lors de l\'actualisation**\n\nUne erreur est survenue. Veuillez réessayer.');
      throw error;
    } finally {
      setEnrichmentInProgress(false);
    }
  }, [handleRefreshObjectiveResults, handleResetSynthesis, generateNewsSynthesis]);

  // ================================
  // CORRECTION: EFFECTS POUR AFFICHAGE SYNTHÈSE
  // ================================

  useEffect(() => {
    // Réagir aux notifications de mise à jour des données
    if (dataUpdated && lastUpdateType) {
      console.log(`📊 Données mises à jour: ${lastUpdateType}`);
      
      // Synchroniser les sources référencées IMMÉDIATEMENT
      if (filteredNews.length > 0) {
        const articlesForSynthesis = selectedObjectiveForNews 
          ? filteredNews.slice(0, 20)
          : filteredNews.slice(0, 15);
        
        console.log(`🔗 Synchronisation sources après ${lastUpdateType}: ${articlesForSynthesis.length} articles`);
        setSynthesisArticles(articlesForSynthesis);
      }
      
      // CORRECTION: Afficher synthèse stockée si disponible
      if (lastUpdateType === 'stored_objective' || lastUpdateType === 'objective_loaded') {
        if (storedObjectiveResults?.synthesis && !newsSynthesis) {
          console.log('📂 Affichage synthèse stockée');
          setNewsSynthesis(storedObjectiveResults.synthesis);
        }
      }
      
      // CORRECTION: Générer synthèse pour découverte automatique réussie
      if (lastUpdateType === 'objective_discovery' && selectedObjectiveForNews && filteredNews.length > 0) {
        console.log('🤖 Génération synthèse pour découverte automatique');
        setTimeout(() => {
          generateNewsSynthesis(true);
        }, 500);
      }
    }
  }, [dataUpdated, lastUpdateType, filteredNews, selectedObjectiveForNews, storedObjectiveResults, newsSynthesis, generateNewsSynthesis]);

  // ================================
  // CORRECTION: EFFECT POUR CHANGEMENT OBJECTIF AVEC SYNTHÈSE STOCKÉE
  // ================================

  useEffect(() => {
    // Pour changement d'objectif: vérifier synthèse stockée
    if (selectedObjectiveForNews && filteredNews.length > 0 && !enrichmentInProgress && !loadingSynthesis) {
      
      // Si c'est un nouvel objectif
      if (selectedObjectiveForNews.id !== lastObjectiveId.current) {
        console.log('🆕 Nouvel objectif détecté');
        lastObjectiveId.current = selectedObjectiveForNews.id;
        
        // Vérifier s'il y a une synthèse stockée
        if (hasStoredResults && storedObjectiveResults?.synthesis && !newsSynthesis) {
          console.log('📂 Utilisation synthèse stockée pour nouvel objectif');
          setNewsSynthesis(storedObjectiveResults.synthesis);
          
          // Synchroniser les sources aussi
          const articlesForSynthesis = filteredNews.slice(0, 20);
          setSynthesisArticles(articlesForSynthesis);
          return;
        }
      }
    }
  }, [
    filteredNews.length, 
    selectedObjectiveForNews?.id, 
    enrichmentInProgress,
    loadingSynthesis,
    hasStoredResults,
    storedObjectiveResults?.synthesis,
    newsSynthesis
  ]);

  // ================================
  // EFFECTS STANDARDS
  // ================================

  useEffect(() => {
    console.log('🔄 Chargement initial');
    loadNews();
    loadUserObjectives();
  }, [userId, objectiveId]);

  useEffect(() => {
    if (objectiveId && objectiveTheme) {
      const relevantTheme = getNewsThemeFromObjective(objectiveTheme);
      setSelectedTheme(relevantTheme);
    }
  }, [objectiveId, objectiveTheme]);

  useEffect(() => {
    if (objectiveId && objectiveTitle) {
      setSelectedObjectiveForNews({
        id: objectiveId,
        title: objectiveTitle,
        theme: objectiveTheme || ''
      });
    }
  }, [objectiveId, objectiveTitle, objectiveTheme]);

  // Effect pour découverte automatique
  useEffect(() => {
    if (shouldTriggerAutoDiscovery) {
      console.log('🎯 Conditions réunies pour découverte automatique');
      
      const timer = setTimeout(() => {
        triggerAutoDiscovery(selectedObjectiveForNews);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [shouldTriggerAutoDiscovery, selectedObjectiveForNews, triggerAutoDiscovery]);

  // Reset état découverte
  useEffect(() => {
    if (selectedObjectiveForNews?.id) {
      console.log('🔄 Nouvel objectif, reset découverte automatique');
      setAutoDiscoveryTriggered(false);
      setShowIndicatorsForObjective(true);
    }
  }, [selectedObjectiveForNews?.id]);

  // Effect changement objectif
  useEffect(() => {
    if (selectedObjectiveForNews?.id !== lastObjectiveId.current) {
      console.log('🔄 Changement objectif, nettoyage tracker');
      handleResetSynthesis();
      lastObjectiveId.current = selectedObjectiveForNews?.id;
    }
  }, [selectedObjectiveForNews?.id, handleResetSynthesis]);

  // ================================
  // COMPOSANTS UTILITAIRES
  // ================================

  const LastAnalysisInfo = () => {
    const storedInfo = getStoredResultsInfo();
    
    if (!storedInfo || !hasStoredResults) {
      return null;
    }

    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-blue-700">
          <Clock className="h-4 w-4" />
          <span className="font-medium">
            Dernière analyse : {new Date(storedInfo.createdAt).toLocaleDateString('fr-FR', { 
              day: 'numeric',
              month: 'short', 
              year: 'numeric',
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </span>
        </div>
      </div>
    );
  };

  const SearchBlock = () => (
    <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-6 mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Search className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">Recherche dans les actualités</h3>
          <p className="text-sm text-gray-600">
            {selectedObjectiveForNews 
              ? semanticAnalysisInfo && semanticAnalysisInfo.totalAnalyzed > 0
                ? 'Affinez les résultats de l\'analyse sémantique IA'
                : 'Filtrez les actualités ciblées par mots-clés'
              : 'Filtrez les actualités par mots-clés'
            }
          </p>
        </div>
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder={selectedObjectiveForNews 
            ? semanticAnalysisInfo && semanticAnalysisInfo.totalAnalyzed > 0
              ? "Affiner les résultats de l'analyse IA..."
              : "Rechercher dans les actualités ciblées..."
            : "Rechercher dans les actualités..."
          }
          value={searchQuery}
          onChange={(e) => handleSearchQueryChange(e.target.value)}
          className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm input-text"
        />
        {searchQuery && (
          <button
            onClick={() => handleSearchQueryChange('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
      {filteredNews.length > 0 && (
        <div className="mt-3 text-sm text-gray-600 flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <span>
            {selectedObjectiveForNews 
              ? semanticAnalysisInfo && semanticAnalysisInfo.totalAnalyzed > 0
                ? `${filteredNews.length} articles pertinents trouvés par analyse sémantique IA`
                : `${filteredNews.length} articles ciblés trouvés`
              : `${filteredNews.length} articles trouvés`
            }
            {searchQuery && ` pour "${searchQuery}"`}
          </span>
        </div>
      )}
    </div>
  );

  // ================================
  // RENDU PRINCIPAL
  // ================================

  return (
    <div className="max-w-6xl mx-auto w-full p-6">
      <style>{FONT_STYLES}</style>
      
      <NewsHeader
        selectedObjectiveForNews={selectedObjectiveForNews}
        availableObjectives={availableObjectives}
        loadingObjectives={loadingObjectives}
        objectivesError={objectivesError}
        isDiscovering={isDiscovering || enrichmentInProgress}
        searchQuery={searchQuery}
        newsSynthesis={newsSynthesis}
        loadingSynthesis={loadingSynthesis}
        synthesisArticles={synthesisArticles}
        filteredNews={filteredNews}
        semanticAnalysisInfo={semanticAnalysisInfo}
        showSemanticAnalysis={showIndicatorsForObjective}
        selectedTheme={selectedTheme}
        themes={NEWS_THEMES}
        onSearchQueryChange={handleSearchQueryChange}
        onObjectiveSelect={setSelectedObjectiveForNews}
        onDiscoverForObjective={handleDiscoverForObjectiveWithCheck}
        onCustomDiscovery={handleCustomDiscoveryInternal}
        onThemeSelect={handleThemeSelect}
        onShowWatchlist={() => setShowWatchlist(true)}
        onGenerateSynthesis={() => generateNewsSynthesis(true)}
        onReadNews={handleReadNews}
        onResetSynthesis={handleResetSynthesis}
      />

      {/* Affichage de la dernière analyse */}
      {selectedObjectiveForNews && showIndicatorsForObjective && <LastAnalysisInfo />}

      <NewsContent
        filteredNews={filteredNews}
        loading={newsLoading}
        error={newsError}
        searchQuery={searchQuery}
        selectedTheme={selectedTheme}
        viewMode={viewMode}
        themes={NEWS_THEMES}
        objectiveId={objectiveId}
        objectiveTheme={objectiveTheme}
        semanticAnalysisInfo={semanticAnalysisInfo}
        getNewsThemeFromObjective={getNewsThemeFromObjective}
        shouldShowEmptyState={shouldShowEmptyState}
        setShowObjectiveDropdown={setShowObjectiveDropdown}
        onReadNews={handleReadNews}
        onThemeSelect={handleThemeSelect}
        onClearSearch={() => setSearchQuery('')}
        onViewModeChange={setViewMode}
        showSearchInHeader={false}
      />

      <NewsThemes
        themes={NEWS_THEMES}
        selectedTheme={selectedTheme}
        newsByTheme={newsByTheme}
        isDiscovering={isDiscovering || enrichmentInProgress}
        objectiveId={objectiveId}
        objectiveTheme={objectiveTheme}
        getNewsThemeFromObjective={getNewsThemeFromObjective}
        onThemeSelect={handleThemeSelect}
        onDiscoverTheme={handleDiscoverTheme}
      />

      <SearchBlock />

      {showWatchlist && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] m-4 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="section-title text-gray-900 flex items-center gap-2">
                <Bell className="h-6 w-6 text-green-600" />
                Service de Veille
              </h2>
              <button
                onClick={() => setShowWatchlist(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
              {userEmail ? (
                <NewsWatchlistManager userId={userId} userEmail={userEmail} />
              ) : (
                <div className="p-6 text-center">
                  <h3 className="section-title text-gray-900 mb-2">Email requis</h3>
                  <p className="main-text text-gray-600 mb-4">
                    Votre adresse email est requise pour utiliser le service de veille.
                  </p>
                  <button
                    onClick={() => setShowWatchlist(false)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors button-text"
                  >
                    Compris
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsPage;
